import * as React from 'react';
import './ForgotPassword.scss';
import Input from 'src/components/common/Input';
import Button from 'src/components/common/Button';
import Alert from './../Alert';
import UserService from 'src/services/user/userService';
import CodeInput from 'src/components/common/CodeInput';
import { validatePasswords } from 'src/store/modules/accountview/actions';
import Loader from 'src/components/common/Loader';
import { goToLogin, goToRedirect } from 'src/utils/navigation';
import ContactSelect from '../ContactSelect';
import env from 'src/shared/env';
enum ForgotPasswordState {
  Forgot,
  Verify,
  SentCode
}

enum AlertType {
  NotFound,
  SentCode,
  InvalidCode,
  Error,
  Success
}

interface IForgotPasswordProps {
  mode: 'forgot' | 'verify';
}

interface IForgotPasswordState {
  loading: boolean;
  input: string;
  state: ForgotPasswordState;
  showAlert: boolean;
  alertType: AlertType;
  verificationCode: string;
  resend: string;
  code: {
    dirty: boolean;
    valid: boolean;
  };
  password: {
    dirty: boolean;
    valid: boolean;
    message: string;
  };
  newPassword: string;
  confirmPassword: string;
  associatedEmail: string;
}

export class ForgotPassword extends React.Component<
  IForgotPasswordProps,
  IForgotPasswordState
> {
  public state: IForgotPasswordState = {
    loading: false,
    input: '',
    resend: '',
    state: ForgotPasswordState.Forgot,
    showAlert: false,
    alertType: AlertType.SentCode,
    verificationCode: null,
    code: {
      dirty: false,
      valid: false
    },
    password: {
      dirty: false,
      valid: false,
      message: ''
    },
    newPassword: '',
    confirmPassword: '',
    associatedEmail: ''
  };

  public componentDidMount() {
    const activeAccount = localStorage.getItem('activeAccount');
    if (activeAccount === 'RPPOWER' || activeAccount === 'RPSUPER') {
      // removing footer for power user
      document.body.classList.add('special-body-easy-notification');
    }
    if (this.props.mode === 'verify') {
      this.setState({ state: ForgotPasswordState.Verify });
    }
  }

  private subset = (value: any) => {
    const array = value.split('@');
    const domain = array[1].split('.');
    let finalString = '';
    finalString = array[0].slice(0, 3);
    for (let i = 3; i < array[0].length; i++) {
      finalString += '*';
    }
    finalString += '@';
    for (let i = 1; i <= domain[0].length - 3; i++) {
      finalString += '*';
    }
    finalString += domain[0].slice(-3);
    finalString += '.' + domain[1];
    return finalString;
  };

  private setStateProm = (newState: any): Promise<void> => {
    return new Promise((resolve, _) => {
      this.setState(newState, () => resolve());
    });
  };

  private handleInput = (value: string) => {
    this.setState({ input: value });
  };

  private handleCloseAlert = () => {
    this.setState({ showAlert: false });
  };

  private handleSubmitForgot = () => {
    this.setStateProm({ loading: true, showAlert: false });

    UserService.forgotPassword(this.state.input)
      .then(res => {
        this.setState({
          loading: false,
          state: ForgotPasswordState.SentCode,
          showAlert: true,
          alertType: AlertType.SentCode,
          resend: '',
          associatedEmail: this.subset(res)
        });
      })
      .catch(err => {
        console.error(err);
        this.setState({
          loading: false,
          showAlert: true,
          alertType: AlertType.NotFound,
          resend: ''
        });
      });
  };
  private reHandleSubmitForgot = () => {
    const send = 'resend';
    this.setState({
      resend: send,
      code: {
        dirty: false,
        valid: false
      }
    });

    this.handleSubmitForgot();
  };
  private reHandleSubmitForgotLink = () => {
    // this.renderSentCode()
    this.reHandleSubmitForgot();
  };
  private handleInputEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.handleSubmitForgot();
    }
  };

  private handleCodeChange = async (value: string) => {
    if (value != null) {
      try {
        const success = await UserService.verifyCode(this.state.input, value);
        if (success) {
          this.setState({
            showAlert: false,
            verificationCode: value,
            code: {
              ...this.state.code,
              dirty: true,
              valid: true
            },
            alertType: 0
          });
        } else {
          this.setState({
            verificationCode: value,
            code: {
              ...this.state.code,
              dirty: false,
              valid: false
            },
            alertType: AlertType.InvalidCode,
            showAlert: true
          });
        }
      } catch (err) {
        console.error(err);
        this.setState({ alertType: AlertType.InvalidCode, showAlert: true });
      }
    } else {
      this.setState({
        code: { ...this.state.code, dirty: false },
        showAlert: false
      });
    }
  };

  private handleNewPassEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.handleSubmitNewPass();
    }
  };

  private handleNewPasswordChange = (value: string) => {
    const validation = validatePasswords(value, this.state.confirmPassword);
    this.setState({
      newPassword: value,
      password: validation
    });
  };

  private handleConfirmPasswordChange = (value: string) => {
    const validation = validatePasswords(value, this.state.newPassword);
    this.setState({
      confirmPassword: value,
      password: validation
    });
  };

  private handleSubmitNewPass = async () => {
    if (
      !this.state.password.dirty ||
      (this.state.password.dirty && !this.state.password.valid)
    ) {
      return;
    }
    await this.setStateProm({ loading: true, showAlert: false });
    try {
      const result = await UserService.forgotPasswordReset(
        this.state.input,
        this.state.verificationCode,
        this.state.newPassword
      );
      await this.setStateProm({
        loading: false,
        showAlert: true,
        alertType: AlertType.Success
      });
      goToRedirect();
    } catch (err) {
      console.error(err);
      await this.setStateProm({
        loading: false,
        showAlert: true,
        alertType: AlertType.Error
      });
    }
  };

  private renderForgot() {
    return (
      <div className="rp-forgotpassword--container">
        <h1>Forgot Password?</h1>
        <p className="rp-forgotpassword--p extra--size">
          Please enter your User ID or Email Address to reset your password.
        </p>
        <p className="rp-forgotpassword--error">
          {env.REACT_APP_BASE_ENVIRONMENT === 'US'
            ? `If you do not know this information, please contact Customer Service at 1-800-666-0313`
            : `If you do not know this information, please contact Customer Service at 1-800-663-6060`}
        </p>
        <Input
          label="User ID or Email Address:"
          onChange={this.handleInput}
          value={this.state.input}
          className="rp-forgotpassword--input"
          onKeyDown={this.handleInputEnter}
        />
        <Button
          label="Submit"
          textTransform="uppercase"
          buttonClass="primary-button rp-forgotpassword--button"
          height="48px"
          action={this.handleSubmitForgot}
          disabled={!this.state.input.length}
        />
      </div>
    );
  }

  private renderVerify() {
    return (
      <div className="rp-forgotpassword--container">
        <h1>2 Step Verification</h1>
        <p className="rp-forgotpassword--p">
          Hi!, we are renovating our database and we need you to renovate your
          password. Please help us and select which contact details we should
          use to send you a code to reset your password.
        </p>
        <ContactSelect onChange={this.handleInput} value={this.state.input} />
        <Button
          label="Submit"
          textTransform="uppercase"
          buttonClass="primary-button rp-forgotpassword--button"
          height="48px"
          action={this.handleSubmitForgot}
          disabled={!this.state.input.length}
        />
      </div>
    );
  }

  private renderPassForm() {
    return (
      <div className="rp-forgotpassword--form">
        <p className="rp-forgotpassword--p">
          Please enter a new password for your user account.
          <br />
          Passwords must be at least 8 characters long.
        </p>
        {/* <p className="rp-forgotpassword--p rp-forgotpassword--error">
          *Please note that this will reset the password for all user accounts
          associated with your email address.
        </p> */}
        <Input
          type="password"
          label="New Password"
          onChange={this.handleNewPasswordChange}
          value={this.state.newPassword}
          className="rp-forgotpassword--input"
          invalid={this.state.password.dirty && !this.state.password.valid}
          invalidMessage={this.state.password.message}
          onKeyDown={this.handleNewPassEnter}
        />
        <Input
          type="password"
          label="Confirm Password"
          onChange={this.handleConfirmPasswordChange}
          value={this.state.confirmPassword}
          className="rp-forgotpassword--input"
          invalid={this.state.password.dirty && !this.state.password.valid}
          invalidMessage={this.state.password.message}
          onKeyDown={this.handleNewPassEnter}
        />
        <Button
          label="Submit"
          textTransform="uppercase"
          buttonClass="primary-button rp-forgotpassword--button"
          height="48px"
          action={this.handleSubmitNewPass}
          disabled={
            !this.state.password.dirty ||
            (this.state.password.dirty && !this.state.password.valid)
          }
        />
      </div>
    );
  }

  private renderSentCode() {
    const cState = this.state.code;

    return (
      <div className="rp-forgotpassword--container rp-forgotpassword--rendersentcode">
        <h1>Password Reset</h1>
        <p className="rp-forgotpassword--p">
          We have sent a 4-digit one-time passcode to the following email
          address
        </p>
        <p className="rp-forgotpassword--p">{this.state.associatedEmail}</p>
        <p className="rp-forgotpassword--p mb-10">
          Please enter the passcode sent to you in the field below. This code is
          only valid for 15 minutes.
        </p>
        <p className="rp-forgotpassword--error">
          {env.REACT_APP_BASE_ENVIRONMENT === 'US'
            ? `If you do not have access to the above email address, please contact Customer Service at 1-800-666-0313`
            : `If you do not have access to the above email address, please contact Customer Service at 1-800-663-6060`}
        </p>
        <h3>Enter your code</h3>
        <CodeInput
          invalid={cState.dirty && !cState.valid}
          success={cState.dirty && cState.valid}
          disabled={cState.dirty && cState.valid}
          resend={this.state.resend}
          onChange={this.handleCodeChange}
          reHandleSubmitForgot={this.reHandleSubmitForgot}
          InvalidCode={AlertType.InvalidCode}
          alertType={this.state.alertType}
        />

        {cState.dirty && cState.valid ? this.renderPassForm() : null}
      </div>
    );
  }

  private renderAlert() {
    const type = this.state.alertType;
    return type === AlertType.SentCode ? (
      <Alert
        type="success"
        children={<span>Please check your email for a one-time passcode.</span>}
        icon="rpds-check-circle"
        onClose={this.handleCloseAlert}
      />
    ) : type === AlertType.NotFound ? (
      <Alert
        type="error"
        children={
          <span>
            Oops! The User ID or E-mail address you entered is not in our
            system. Please review and try again.
          </span>
        }
        icon="rpds-error"
        onClose={this.handleCloseAlert}
      />
    ) : // type === AlertType.InvalidCode ? (
    //   <Alert
    //     type="error"
    //     children={
    //       <span>
    //         Oops! The code you have entered is either invalid or expired, please click
    //         <a href="javascript:void(0)" onClick={this.reHandleSubmitForgotLink}>
    //           here
    //         </a>{' '}
    //         to receive a new code
    //         {/* Oops! Your code has might invalid (or) expired, please click resend
    //         code to receive a new code. */}
    //       </span>
    //     }
    //     icon="rpds-error"
    //     onClose={this.handleCloseAlert}
    //   />
    // ) :
    type === AlertType.Error ? (
      <Alert
        type="error"
        children={
          <span>
            There was an error, please try again later or contact support.
          </span>
        }
        icon="rpds-error"
        onClose={this.handleCloseAlert}
      />
    ) : type === AlertType.Success ? (
      <Alert
        type="success"
        children={
          <span>
            <b>Password successfully</b> updated. You will be redirected to the
            sign in page.
          </span>
        }
        icon="rpds-check-circle"
        onClose={this.handleCloseAlert}
      />
    ) : null;
  }

  public render() {
    const state = this.state.state;
    return (
      <div className="rp-forgotpassword">
        {this.state.loading ? <Loader /> : null}
        <div className="rp-forgotpassword--alert">
          {this.state.showAlert ? this.renderAlert() : null}
        </div>
        <div className="rp-forgotpassword--content">
          {state === ForgotPasswordState.Forgot
            ? this.renderForgot()
            : state === ForgotPasswordState.Verify
            ? this.renderVerify()
            : state === ForgotPasswordState.SentCode
            ? this.renderSentCode()
            : null}
        </div>
        {/* <p className="rp-forgotpassword--para">
          (*** - 1-800-666-0313 for US, 1-800-663-6060 for Canada)
        </p> */}
      </div>
    );
  }
}
